<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useDark, useResizeObserver } from '@vueuse/core';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { useWorkspaceContext } from '@/composables/use-workspace-context';
import { usePanelHandle } from '@/composables/use-panels';
import { terminalTheme, monoFont, monoFontSize } from '@/composables/use-editor-theme';
import type { ChannelStream } from '@ts-ssh2/core';

const ctx = useWorkspaceContext();
const isDark = useDark();

const handle = usePanelHandle('Terminal');

// focus xterm when the panel tab is clicked
handle.onFocus = () => {
	terminal?.focus();
};

const terminalRef = ref<HTMLElement | null>(null);

let terminal: Terminal | null = null;
let fitAddon: FitAddon | null = null;
let channel: ChannelStream | null = null;
let stdinWriter: WritableStreamDefaultWriter<Uint8Array> | null = null;
let stdoutReader: ReadableStreamDefaultReader<Uint8Array> | null = null;
let stderrReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

function createTerminal() {
	terminal = new Terminal({
		cursorBlink: true,
		fontFamily: monoFont(),
		fontSize: monoFontSize(),
		theme: terminalTheme(),
	});

	fitAddon = new FitAddon();
	terminal.loadAddon(fitAddon);
	terminal.onTitleChange((title) => (handle.title = title));
}

async function pipeOutput(reader: ReadableStreamDefaultReader<Uint8Array>) {
	try {
		while (true) {
			const { done, value } = await reader.read();
			if (done) break;
			terminal?.write(value);
		}
	} catch {
		// stream closed or errored — expected on disconnect
	}
}

function wireStreams(ch: ChannelStream) {
	channel = ch;
	stdinWriter = ch.stdin.getWriter();
	stdoutReader = ch.stdout.getReader();
	stderrReader = ch.stderr.getReader();

	pipeOutput(stdoutReader);
	pipeOutput(stderrReader);

	terminal!.onData((data: string) => {
		const encoded = new TextEncoder().encode(data);
		stdinWriter?.write(encoded).catch(() => {});
	});

	terminal!.onResize(({ cols, rows }) => {
		ch.resize(cols, rows).catch(() => {});
	});

	ch.exitStatus.then((code) => {
		if (code < 0) {
			terminal?.write('\r\n\x1b[31m--- connection lost ---\x1b[0m\r\n');
		} else {
			terminal?.write('\r\n\x1b[33m--- session ended ---\x1b[0m\r\n');
		}
	});
}

function fit() {
	if (fitAddon && terminalRef.value) {
		fitAddon.fit();
	}
}

// throttled fit — collapses rapid ResizeObserver calls into one trailing fit.
// with display:none on hidden panels, ResizeObserver won't fire while hidden and will
// automatically fire with correct dimensions when the panel becomes visible again.
let resizeTimer: ReturnType<typeof setTimeout> | null = null;
const RESIZE_THROTTLE_MS = 80;

const throttledFit = () => {
	if (resizeTimer) return;
	resizeTimer = setTimeout(() => {
		resizeTimer = null;
		fit();
	}, RESIZE_THROTTLE_MS);
};

useResizeObserver(terminalRef, () => throttledFit());

onMounted(async () => {
	if (!terminalRef.value) return;

	createTerminal();
	terminal!.open(terminalRef.value);

	// open our own shell session
	try {
		const dims = fitAddon!.proposeDimensions();
		const ch = await ctx.openShell({
			cols: dims?.cols ?? 80,
			rows: dims?.rows ?? 24,
		});
		wireStreams(ch);
	} catch {
		terminal?.write('\r\n\x1b[31m--- failed to open shell ---\x1b[0m\r\n');
	}
});

onUnmounted(() => {
	if (resizeTimer) clearTimeout(resizeTimer);
	channel?.close().catch(() => {});
	terminal?.dispose();
	terminal = null;
	fitAddon = null;
	channel = null;
});

// switch terminal theme when dark mode changes
watch(isDark, () => {
	if (terminal) {
		terminal.options.theme = terminalTheme();
	}
});
</script>

<template>
	<div ref="terminalRef" class="size-full" />
</template>

<style>
@import '@xterm/xterm/css/xterm.css';

.xterm {
	padding: 8px;
	height: 100%;
}
</style>
